import { useMemo } from 'react';

import {
  getCartServiceFeeDescriptions,
  getCartCouponCode,
  getCartCouponDiscountCents,
  getCartCouponPercentLabel,
  getCartDiscountCents,
  getCartProductCount,
  getCartSubtotalCents,
  getCartTotalAmountCents,
  type Cart,
} from '@src/domains/cart';
import { getCouponAffectedProductNames, shouldShowCouponAffectedProducts } from '@src/domains/coupons';
import { shouldShowServiceFee } from '@src/domains/service-fees';
import { cartSummaryHasDetails } from '@src/features/cart/summary/core/cartSummaryHasDetails';
import useFeatureFlag from '@src/hooks/useFeatureFlag';
import { useAppSelector } from '@src/core/hooks';
import { appInfoSelector } from '@src/stores/app';

export type UseCartSummaryResult = {
  productCount: number;
  subtotalCents: number;
  totalAmountCents: number;
  discountCents: number;
  couponCode?: string;
  couponDiscountCents: number;
  couponPercentLabel?: string;
  affectedProductNames: string[];
  showAffectedProducts: boolean;
  showServiceFee: boolean;
  serviceFeeAmountCents: number;
  serviceFeeDescription?: string;
  hasDetails: boolean;
};

export const useCartSummary = (cart: Cart | null | undefined): UseCartSummaryResult => {
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const sellerAppId = useAppSelector(appInfoSelector)?.id;
  const serviceFeeFeatureEnabled = isFeatureFlagEnabled('PR-647-service-fee');

  const totalDiscountCents = getCartDiscountCents(cart);
  const couponDiscountCents = getCartCouponDiscountCents(cart, totalDiscountCents);
  const showServiceFee = shouldShowServiceFee(
    cart?.costPreview?.serviceFeeCurrencyInfo?.amountCents,
    serviceFeeFeatureEnabled,
  );
  const serviceFeeAmountCents = cart?.costPreview?.serviceFeeCurrencyInfo?.amountCents ?? 0;

  const serviceFeeDescription = useMemo(() => {
    const descriptions = getCartServiceFeeDescriptions(cart?.items ?? []);

    return descriptions.length > 0 ? descriptions.join(', ') : undefined;
  }, [cart?.items]);
  const affectedProductNames = useMemo(
    () => getCouponAffectedProductNames(cart?.coupon, cart?.items ?? [], sellerAppId),
    [cart?.coupon, cart?.items, sellerAppId],
  );

  return {
    productCount: getCartProductCount(cart),
    subtotalCents: getCartSubtotalCents(cart),
    totalAmountCents: getCartTotalAmountCents(cart),
    discountCents: totalDiscountCents,
    couponCode: getCartCouponCode(cart),
    couponDiscountCents,
    couponPercentLabel: getCartCouponPercentLabel(cart),
    affectedProductNames,
    showAffectedProducts: shouldShowCouponAffectedProducts(cart?.coupon, cart?.items ?? [], sellerAppId),
    showServiceFee,
    serviceFeeAmountCents,
    serviceFeeDescription,
    hasDetails: cartSummaryHasDetails(cart, serviceFeeFeatureEnabled),
  };
};

export default useCartSummary;
